import React, { useState, useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import type { Brand } from '../../../types/brand';
import { BrandSelector } from '../../layout/BrandSelector';

interface AnalyticsProps {
  applicants: {
    referralSource: string | null;
    submissionDate: Date;
    brandId: string;
  }[];
  brands: Brand[];
  selectedBrand: Brand | null;
  onSelectBrand: (brand: Brand) => void;
  onCreateNew: () => void;
}

const theme = {
  background: 'transparent',
  axis: {
    fontSize: '14px',
    tickColor: '#eee',
    ticks: {
      line: {
        stroke: '#555555'
      },
      text: {
        fill: '#ffffff'
      }
    },
    legend: {
      text: {
        fill: '#aaaaaa'
      }
    }
  },
  grid: {
    line: {
      stroke: '#555555'
    }
  },
  legends: {
    text: {
      fill: '#ffffff'
    }
  },
  labels: {
    text: {
      fill: '#ffffff',
      fontSize: 14,
      fontWeight: 500
    }
  },
  tooltip: {
    container: {
      background: '#1f2937',
      color: '#ffffff',
      fontSize: '12px',
      borderRadius: '6px',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    }
  }
};

export function Analytics({ applicants, brands, selectedBrand, onSelectBrand, onCreateNew }: AnalyticsProps) {
  const [timeframe, setTimeframe] = useState<'all' | 'today' | 'yesterday' | '7days' | '30days'>('today');

  const filteredApplicants = useMemo(() => {
    // First filter by brand if selected
    let filtered = selectedBrand 
      ? applicants.filter(applicant => applicant.brandId === selectedBrand.id)
      : applicants;

    // Then filter by timeframe
    if (timeframe === 'all') return filtered;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const cutoff = new Date();
    
    switch (timeframe) {
      case 'today':
        return filtered.filter(applicant => {
          const submissionDate = new Date(applicant.submissionDate);
          return submissionDate >= startOfToday;
        });
      case 'yesterday':
        return filtered.filter(applicant => {
          const submissionDate = new Date(applicant.submissionDate);
          return submissionDate >= startOfYesterday && submissionDate < startOfToday;
        });
      case '7days':
        cutoff.setDate(now.getDate() - 7);
        break;
      case '30days':
        cutoff.setDate(now.getDate() - 30);
        break;
    }

    return filtered.filter(applicant => {
      const submissionDate = new Date(applicant.submissionDate);
      return submissionDate >= cutoff;
    });
  }, [applicants, timeframe, selectedBrand]);

  const referralData = useMemo(() => {
    const sources: Record<string, number> = {};
    
    filteredApplicants.forEach(applicant => {
      const source = applicant.referralSource || 'Unknown';
      sources[source] = (sources[source] || 0) + 1;
    });

    return Object.entries(sources)
      .map(([id, value]) => ({
        id,
        label: id,
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredApplicants]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-100">Referral Sources</h2>
        <div className="flex items-center space-x-4">
          <BrandSelector
            brands={brands}
            selectedBrand={selectedBrand}
            onSelectBrand={onSelectBrand}
            onCreateNew={onCreateNew}
          />
          <div className="flex space-x-2">
            {[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: '7days', label: 'Last 7 Days' },
              { value: '30days', label: 'Last 30 Days' },
              { value: 'all', label: 'All Time' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setTimeframe(option.value as any)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                  ${timeframe === option.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6" style={{ height: '600px' }}>
        <ResponsivePie
          data={referralData}
          margin={{ top: 40, right: 120, bottom: 40, left: 80 }}
          innerRadius={0.5}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          colors={{ scheme: 'paired' }}
          borderWidth={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]]
          }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#ffffff"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor="#ffffff"
          theme={theme}
          legends={[
            {
              anchor: 'right',
              direction: 'column',
              justify: false,
              translateX: 100,
              translateY: 0,
              itemsSpacing: 10,
              itemWidth: 100,
              itemHeight: 24,
              itemTextColor: '#ffffff',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              symbolSize: 18,
              symbolShape: 'circle'
            }
          ]}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-100 mb-4">Summary</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Total Applicants</p>
              <p className="text-2xl font-semibold text-gray-100">{filteredApplicants.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Top Referral Source</p>
              <p className="text-2xl font-semibold text-gray-100">
                {referralData[0]?.id || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-100 mb-4">Detailed Breakdown</h3>
          <div className="space-y-2">
            {referralData.map(({ id, value }) => (
              <div key={id} className="flex justify-between items-center">
                <span className="text-gray-300">{id}</span>
                <span className="text-gray-400">{value} applicants</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 